import type { DataProvider, Identifier } from "ra-core";

import {
  dealUpdates,
  groupDealsByStage,
  moveDealInColumns,
  type DealSlot,
  type PipelineStage,
} from "./dealData";

export interface DealMove {
  dealId: Identifier;
  fromStageId: number;
  toStageId: number;
  // the deal goes before this one; null: at the end of the column
  beforeId: Identifier | null;
  // reason and note when the deal goes to a lost stage (D-036)
  lost?: { lost_reason_id: number; lost_reason: string | null };
  // next action when a deal without one goes to an open stage (D-037)
  nextActionAt?: string;
}

// Writes a move of a deal: stage_id and index of the moved deal, then the
// index of the others in the two columns. The columns are read again from
// the database, with every deal of the stages: the board may be filtered.
// The moved deal goes first, so a move the database refuses (a lost deal
// without a reason, an open deal without a next action) changes nothing.
export const persistDealMove = async (
  dataProvider: DataProvider,
  stages: PipelineStage[],
  move: DealMove,
) => {
  const stageIds = [...new Set([move.fromStageId, move.toStageId])];
  const { data: deals } = await dataProvider.getList<
    DealSlot & { id: Identifier }
  >("deals", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "index", order: "ASC" },
    filter: {
      "stage_id@in": `(${stageIds.join(",")})`,
      "archived_at@is": null,
    },
  });
  const columns = groupDealsByStage(
    deals,
    stages.filter((stage) => stageIds.includes(stage.id)),
  );
  const after = moveDealInColumns(
    columns,
    move.dealId,
    move.toStageId,
    move.beforeId,
  );
  const [first, ...others] = dealUpdates(deals, after, move.dealId);
  if (!first) return;
  const previous = (id: Identifier) => deals.find((deal) => deal.id === id)!;

  await dataProvider.update("deals", {
    id: first.id,
    data: {
      ...first.data,
      ...(move.lost ?? {}),
      ...(move.nextActionAt ? { next_action_at: move.nextActionAt } : {}),
    },
    previousData: previous(first.id),
  });
  await Promise.all(
    others.map((update) =>
      dataProvider.update("deals", {
        id: update.id,
        data: update.data,
        previousData: previous(update.id),
      }),
    ),
  );
};
